import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import finalLogo from '../../assets/images/final-logo.svg';
import Button from '../../components/shared/Button';
import CustomCheckbox from '../../components/shared/CustomCheckbox';
import { AdminService, AuthStorage } from '../../services';
import { Toast } from '../../components/shared/Toast';
import { useAuthContext } from '../../hooks/useAuth';

const styles = {
	section: 'flex items-center justify-center min-h-screen bg-bgColor',
	Column: 'w-full max-w-md lg:w-[520px] px-6',
	imageStyle: 'h-12 w-[260px] mb-20',
	mainHeading: 'text-left text-main leading-39 font-semibold text-dark-blue mb-3',
	subHeading: 'text-left text-lg leading-100 text-gray mb-[50px] font-medium',
	label: 'block text-base leading-100 font-medium text-dark-blue mb-3',
	input:
		'w-full pl-3.5 py-3.5 border-1p3 border-lightGrey text-dark-blue rounded-10p placeholder-gray',
	checkbox: 'flex items-center justify-between mb-[60px]',
	agreeTerms: 'ml-1.5 block text-base leading-21 text-gray font-medium',
	forgotPass: 'font-medium text-base leading-21 text-dark-blue',
	signUp: 'text-left text-base leading-21 font-medium text-gray mt-5'
};

const AdminLoginPage = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);
	const [error, setError] = useState('');
	const navigate = useNavigate();
	const { login } = useAuthContext();
	const [isLoading, setIsLoading] = useState(false);
	const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
	const isPasswordValid = (value) => value.trim().length > 0;
	const isFormValid = isValidEmail(email.trim()) && isPasswordValid(password.trim());

	useEffect(() => {
		const rememberedEmail = AuthStorage.getRememberedEmail();
		if (rememberedEmail) {
			setEmail(rememberedEmail);
			setRememberMe(true); // Also set rememberMe to true if email is found
		}
	}, []);

	const navigateToDashboard = (routes) => {
		if (routes.includes('/admin/dashboard')) {
			navigate('/admin/dashboard');
		} else if (routes.includes('/sub-admin/dashboard')) {
			navigate('/sub-admin/dashboard');
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');

		const trimmedEmail = email.trim();
		if (!isValidEmail(trimmedEmail)) {
			Toast.error('Please enter a valid email address.');
			return;
		}
		setIsLoading(true);

		try {
			const response = await AdminService.login(email, password);

			if (response.status === 200 && response.token) {
				AuthStorage.setToken(response.token);
				// Pass accessibleRoutes separately as the third argument
				login(response.token, response.user, response.user?.accessibleRoutes || []);
				Toast.success("Welcome back! You're now signed in.");
				if (rememberMe) {
					AuthStorage.setRememberedEmail(trimmedEmail);
				} else {
					AuthStorage.removeRememberedEmail();
				}
				navigateToDashboard(response.user?.accessibleRoutes || []); // Call navigateToDashboard here
			} else if (response.status === 403) {
				const message = response.message || 'Your account is inactive. Please contact support.';
				Toast.error(message);
			} else if (response.status === 401) {
				const message = response.message || 'Invalid email or password. Please try again.';
				Toast.error(message);
			} else {
				const message = response.message || 'Login failed. Please try again.';
				Toast.error(message);
				console.error('Login failed:', response);
			}
		} catch (error) {
			console.error('Login error:', error);
			setError('Network error. Please check your connection and try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={styles.section}>
			{/* Centered Login Form */}
			<div className={styles.Column}>
				<div>
					<img className={styles.imageStyle} src={finalLogo} alt="WriteForm Logo" />
					<h2 className={styles.mainHeading}>Welcome back</h2>
					<p className={styles.subHeading}>Welcome back! Please enter your details.</p>
				</div>

				<form onSubmit={handleSubmit}>
					{error && <p className="text-red-500 text-center mb-4">{error}</p>}
					<div className="mb-[30px]">
						<label htmlFor="email-address" className={styles.label}>
							Email Address <span className="text-red-500">*</span>
						</label>
						<input
							id="email-address"
							name="email"
							type="email"
							autoComplete="email"
							required
							className={styles.input}
							placeholder="Email Address *"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>

					<div className="mb-3">
						<label htmlFor="password" className={styles.label}>
							Password <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								id="password"
								name="password"
								type={showPassword ? 'text' : 'password'}
								autoComplete="current-password"
								required
								className={styles.input}
								placeholder="Password *"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
							<button
								type="button"
								onClick={() => setShowPassword((s) => !s)}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
								className="absolute inset-y-0 right-0 px-3 flex items-center"
							>
								{/* Toggle eye icons */}
								{showPassword ? (
									<svg
										width="23"
										height="23"
										viewBox="0 0 23 23"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g opacity="0.5">
											<path
												d="M11.416 5.80859C14.9982 5.80868 18.0305 8.16279 19.0498 11.4082C18.0306 14.6537 14.9983 17.0087 11.416 17.0088C7.83362 17.0088 4.80143 14.6538 3.78223 11.4082C4.80154 8.16279 7.83375 5.80859 11.416 5.80859ZM11.416 8.20898C10.5675 8.20898 9.75341 8.54557 9.15332 9.14551C8.5533 9.74553 8.21592 10.5597 8.21582 11.4082C8.21582 12.2568 8.55327 13.0708 9.15332 13.6709C9.75344 14.271 10.5673 14.6084 11.416 14.6084C12.2646 14.6083 13.0786 14.271 13.6787 13.6709C14.2786 13.0708 14.6162 12.2568 14.6162 11.4082C14.6161 10.5597 14.2787 9.74553 13.6787 9.14551C13.0786 8.5455 12.2646 8.20903 11.416 8.20898ZM11.416 9.80859C11.8403 9.80862 12.2469 9.9774 12.5469 10.2773C12.8468 10.5773 13.0155 10.984 13.0156 11.4082C13.0156 11.8325 12.8469 12.24 12.5469 12.54C12.2469 12.8399 11.8402 13.0088 11.416 13.0088C10.9917 13.0088 10.5842 12.8401 10.2842 12.54C9.98421 12.24 9.81641 11.8325 9.81641 11.4082C9.81651 10.9841 9.98437 10.5773 10.2842 10.2773C10.5842 9.97729 10.9917 9.80859 11.416 9.80859Z"
												fill="#180A63"
											/>
										</g>
									</svg>
								) : (
									<svg
										width="23"
										height="23"
										viewBox="0 0 23 23"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g opacity="0.5">
											<path
												d="M18.6995 18.0903L17.6389 19.1509L3.49634 5.0083L4.55688 3.94775L18.6995 18.0903ZM7.96899 10.481C7.89421 10.7539 7.85477 11.0378 7.85474 11.3257C7.85474 12.1743 8.19218 12.9883 8.79224 13.5884C9.39235 14.1885 10.2062 14.5259 11.0549 14.5259C11.3427 14.5259 11.6259 14.4853 11.8987 14.4106L13.8948 16.4067C13.0122 16.742 12.0551 16.9262 11.0549 16.9263C7.47253 16.9263 4.44034 14.5713 3.42114 11.3257C3.82335 10.0451 4.53961 8.90392 5.47876 7.99072L7.96899 10.481ZM11.0549 5.72607C14.6372 5.72616 17.6695 8.08027 18.6887 11.3257C18.2692 12.6616 17.5077 13.8458 16.5081 14.7778L14.0842 12.354C14.1958 12.0257 14.2551 11.6788 14.2551 11.3257C14.255 10.4771 13.9177 9.66301 13.3176 9.06299C12.7176 8.46298 11.9035 8.12651 11.0549 8.12646C10.7018 8.12646 10.3548 8.1851 10.0266 8.29639L8.04419 6.31396C8.97372 5.93606 9.98964 5.72607 11.0549 5.72607ZM10.0842 12.5962C10.0281 12.5533 9.97356 12.508 9.9231 12.4575C9.87263 12.407 9.82724 12.3525 9.78442 12.2964L10.0842 12.5962ZM11.5286 9.79834C11.7743 9.87466 12.0004 10.0095 12.1858 10.1948C12.3708 10.3798 12.5049 10.6058 12.5813 10.8511L11.5286 9.79834Z"
												fill="#180A63"
											/>
										</g>
									</svg>
								)}
							</button>
						</div>
					</div>

					<div className={styles.checkbox}>
						<div className="flex items-center">
							<CustomCheckbox
								id="remember-me"
								checked={rememberMe}
								onChange={(e) => setRememberMe(e.target.checked)}
							/>
							<label htmlFor="agree-terms" className={styles.agreeTerms}>
								Remember me
							</label>
						</div>

						<div>
							<a href="/admin/forgot-password" className={styles.forgotPass}>
								Forgot password?
							</a>
						</div>
					</div>

					<div>
						<Button type="submit" disabled={isLoading || !isFormValid} variant="default">
							{isLoading ? 'Logging in...' : 'Login'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default AdminLoginPage;
